import { useId, useRef } from "react";
import { createPortal } from "react-dom";
import { FiTrash2 } from "react-icons/fi";
import { AppButton } from "@/components/ui/primitives";
import { useDialogBehavior } from "@/hooks/useDialogBehavior";
import { formatFlowAmount, type AmountFlow } from "@/utils/formatters";

export interface DeleteTransactionDialogProps {
  open: boolean;
  /** Título de la confirmación, p. ej. "¿Eliminar este movimiento?". */
  title: string;
  /** Consecuencia en una o dos frases: efecto en el saldo y si se puede deshacer. */
  text: string;
  summary: { name: string; amount: number; flow: AmountFlow; meta?: string };
  confirmLabel: string;
  cancelLabel: string;
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirmación de eliminar un movimiento: alertdialog de 480px centrado (hoja inferior debajo de md). */
export function DeleteTransactionDialog({
  open,
  title,
  text,
  summary,
  confirmLabel,
  cancelLabel,
  isPending = false,
  onConfirm,
  onCancel,
}: DeleteTransactionDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  // While the delete request is in flight the dialog cannot be dismissed.
  const handleCancel = () => {
    if (!isPending) onCancel();
  };
  useDialogBehavior({ open, onClose: handleCancel, containerRef: panelRef, initialFocusRef: cancelRef });

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center md:items-center md:p-6">
      <div className="absolute inset-0 bg-[var(--app-overlay)] backdrop-blur-sm" onClick={handleCancel} aria-hidden="true" />
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className="relative flex w-full flex-col gap-4 rounded-t-[var(--ds-radius-lg)] bg-[var(--app-surface)] p-6 text-[var(--app-text)] shadow-[var(--app-shadow-strong)] md:w-[480px] md:rounded-[var(--ds-radius-xl)] md:p-8"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--ds-card-flame)] text-[var(--ds-on-card)]" aria-hidden="true">
          <FiTrash2 size={24} />
        </span>
        <div>
          <h2 id={titleId} className="text-[28px] font-medium leading-[1.1] tracking-[-0.03em]">{title}</h2>
          <p id={descriptionId} className="mt-1 text-[15px] leading-[22px] text-[var(--app-muted-text)]">{text}</p>
        </div>

        <div className="flex items-center gap-3 rounded-[var(--ds-radius-md)] bg-[var(--app-surface-soft)] px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-medium leading-[22px]">{summary.name}</p>
            {summary.meta ? <p className="text-[13px] leading-[18px] text-[var(--app-muted-text)]">{summary.meta}</p> : null}
          </div>
          <p className="shrink-0 text-lg font-medium leading-6 tabular-nums">
            {formatFlowAmount(summary.amount, summary.flow)}
          </p>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:justify-end">
          <AppButton ref={cancelRef} tone="secondary" fullWidth className="md:w-auto" onClick={handleCancel} disabled={isPending}>
            {cancelLabel}
          </AppButton>
          <AppButton tone="danger" fullWidth className="md:w-auto" onClick={onConfirm} disabled={isPending} isLoading={isPending}>
            {confirmLabel}
          </AppButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}
